import moment, { Moment } from 'moment';
import { useAppSelector } from 'redux/store';
import { getTimezoneAbbreviation } from 'helpers/timezoneUtils';

/**
 * Hook to get a date converted to the user's timezone
 * Priority: User timezone → Organization timezone → UTC
 */
export const useGetDateWithTimeZone = (date?: Moment): Moment => {
  // User timezone takes priority over organization timezone
  const userTimeZone = useAppSelector(state => state.authSlice.user?.timeZone);
  const orgTimeZone = useAppSelector(
    state => state.authSlice.user?.organization?.timeZone,
  );

  // Fallback chain: User → Org → UTC
  const timeZone = userTimeZone || orgTimeZone || 'UTC';

  const inputDate = date?.toDate() || new Date();

  const invdate = new Date(
    inputDate.toLocaleString('en-US', {
      timeZone,
    }),
  );
  const diff = inputDate.getTime() - invdate.getTime();
  const dateWithTimeZone = new Date(inputDate.getTime() - diff);
  return moment(dateWithTimeZone);
};

/**
 * Hook to get the user's current timezone identifier and abbreviation
 * Priority: User timezone → Organization timezone → UTC
 */
export const useUserTimezone = () => {
  const userTimeZone = useAppSelector(state => state.authSlice.user?.timeZone);
  const orgTimeZone = useAppSelector(
    state => state.authSlice.user?.organization?.timeZone,
  );

  // Fallback chain: User → Org → UTC
  const timeZone = userTimeZone || orgTimeZone || 'UTC';
  const abbreviation = getTimezoneAbbreviation(timeZone);

  return {
    timeZone,
    abbreviation,
    isUserTimezone: !!userTimeZone,
    isOrgTimezone: !userTimeZone && !!orgTimeZone,
  };
};
